/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable max-len */
import React, {
    useContext,
    useCallback,
    useState,
    useEffect
} from 'react';
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import MLTooltipComponent from '../../../../modules-react/ml-components-platform-react/MLTooltipComponent/MLTooltipComponent.jsx';
import metadata from './PolicySummaryConfirmationBoxComponent.metadata.json5';
import messages from './PolicySummaryConfirmationBoxComponent.messages';
import styles from './PolicySummaryConfirmationBoxComponent.module.scss';


import { IntlContext } from '@jutro/legacy/locale';


function PolicySummaryConfirmationBoxComponent(props) {
    const {
        ampMode,
        qbMode,
        model: policyDetails,
        startPrint,
        editPayment,
        editEffectiveDate,
        cancelChanges,
        makeMoreChanges,
        submitChanges,
        detailsLoaded,
        updateStartPrint
    } = props;
    const intl = useContext(IntlContext);
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const [showAllDiscounts, updateShowAllDiscounts] = useState(false);

    const {
        MLAnnuallyPayPlanPublicId,
        MLMortgageePayPlanPublicId,
        MLMonthlyPayPlanPublicId,
        MLBiWeeklyPayPlanPublicId,
        MLTwoPayPlanPublicId,
        MLTwoPayAnnualPlanPublicId,
        MLFourPayAnnualPlanPublicId
    } = appConfig.mlPaymentPlan;

    useEffect(() => {
        if (startPrint && !showAllDiscounts) {
            updateShowAllDiscounts(true);
        }
        if (startPrint && showAllDiscounts) {
            window.print();
            updateStartPrint(false);
        }
        return _.noop;
    }, [showAllDiscounts, startPrint, updateStartPrint]);

    const defaultDisplayedDiscountNo = 3;

    const termTypes = {
        Annual: messages.termTypeYear,
        HalfYear: messages.termTypeHalfYear
    };

    const paymentSchedules = {
        [MLAnnuallyPayPlanPublicId]: messages.annualPayment,
        [MLMortgageePayPlanPublicId]: messages.payByMortgagee,
        [MLMonthlyPayPlanPublicId]: messages.monthlyPayment,
        [MLBiWeeklyPayPlanPublicId]: messages.twiceAMonth,
        [MLTwoPayPlanPublicId]: messages.twoPay,
        [MLTwoPayAnnualPlanPublicId]: messages.twoPay,
        [MLFourPayAnnualPlanPublicId]: messages.fourPay
    };

    const totalDiscounts = _.get(policyDetails, 'paymentDetails.totalDiscounts.value', undefined);

    const paymentScheduleDisplayKey = useCallback(() => {
        const selectedPaymentPlanId = _.get(policyDetails, 'paymentDetails.selectedPaymentPlanId.value', undefined);
        const termType = _.get(policyDetails, 'termType_ML.value.code', undefined);
        if (selectedPaymentPlanId === MLAnnuallyPayPlanPublicId && termType === "HalfYear") {
            return translator(messages.semiAnnualPayment);
        }
        if (!_.isUndefined(selectedPaymentPlanId)) {
            return translator(paymentSchedules[selectedPaymentPlanId]);
        }
    }, [paymentSchedules, policyDetails, translator]);

    const formatAmountString = useCallback((amount) => {
        try {
            return intl.formatNumber(amount.amount, { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const getTermTypeDisplay = useCallback(() => {
        const termType = _.get(policyDetails, 'termType_ML.value.code', undefined);
        return termTypes[termType];
    }, [policyDetails, termTypes]);

    const convertLocalDateToDate = (localDate) => {
        const parsedDate = localDate ? new Date(localDate.year, localDate.month, localDate.day) : '';
        return parsedDate ? moment(parsedDate).format('MM/DD/YYYY') : '';
    };

    const getPaymentFrequency = useCallback(() => {
        let display;
        const selectedPaymentPlanId = _.get(policyDetails, 'paymentDetails.selectedPaymentPlanId.value', undefined);
        switch (selectedPaymentPlanId) {
            case MLAnnuallyPayPlanPublicId:
                display = translator(messages.paymentPlanYear);
                break;
            case MLMonthlyPayPlanPublicId:
                display = translator(messages.paymentPlanMonth);
                break;
            case MLBiWeeklyPayPlanPublicId:
                display = translator(messages.paymentPlanTwoWeeks);
                break;
            default:
                display = undefined;
        }
        return display;
    }, [MLAnnuallyPayPlanPublicId, MLBiWeeklyPayPlanPublicId, MLMonthlyPayPlanPublicId, policyDetails, translator]);

    const paymentFrequency = getPaymentFrequency();

    const renderPremium = useCallback(() => {
        const scheduledPremium = _.get(policyDetails, 'paymentDetails.scheduledPremium_ML.value', undefined);
        return (
            <>
                {`${formatAmountString(scheduledPremium)}`}
                {!_.isUndefined(paymentFrequency) && qbMode && (
                    <span className={styles.summaryPremium}>
                        /
                        {`${paymentFrequency}`}
                    </span>
                )
                }
            </>
        );
    }, [formatAmountString, paymentFrequency, policyDetails]);

    const renderTotalPremium = useCallback(() => {
        const currentTotal = _.get(policyDetails, 'paymentDetails.currentTotal.value', undefined);
        return (
            <>
                <span className="font-weight-bold">{qbMode ? translator(messages.totalPremium) : translator(messages.premiumBeforeChange)}</span>
                {formatAmountString(currentTotal)}
                /
                {translator(getTermTypeDisplay())}
            </>     
        );
    }, [formatAmountString, getTermTypeDisplay, policyDetails, qbMode, translator]);

    const renderCost = useCallback((cost, message) => {
        return (
            <>
                <span className="font-weight-bold">{translator(message)}</span>
                {formatAmountString(cost)}
            </>  
        )
    }, [formatAmountString, translator])

    const renderEffectiveDateClick = useCallback(() => {
        return <>
            <a onClick={editEffectiveDate}>{convertLocalDateToDate(_.get(policyDetails, 'effective_ML.value', undefined))}</a>
            <MLTooltipComponent tooltipWrapperClassName={styles.effectiveDateTooltip} tooltipHeader={translator(messages.effectiveDateHelpTitle)} tooltipContent={translator(messages.effectiveDateHelpText)}/>
        </>
    })

    const getRatingPaymentMethodDisplay = useCallback(() => {
        const ratingPaymentMethod = _.get(policyDetails, 'paymentDetails.ratingPaymentMethod_ML.value', undefined);
        if (!_.isUndefined(ratingPaymentMethod)) {
            const ratingPaymentMethodTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('RatingPaymentMethod_ML');
            const ratingPaymentMethodDisplay = ratingPaymentMethodTypelist.getCode(ratingPaymentMethod.code).name;
            return translator({
                id: ratingPaymentMethodDisplay,
                defaultMessage: ratingPaymentMethodDisplay
            });
        }
        return '';
    }, [policyDetails, translator, viewModelService.productMetadata]);

    const appendLabelAndVal = (label, val) => {
        return (
            <>
                <span className="font-weight-bold">{label}</span>
                {val}
            </>
        );
    };

    const discountsListOverride = useCallback(() => {
        const discounts = _.get(policyDetails, 'discounts.value', []);
        const listOverride = discounts.map((discount, index) => {
            return {
                [`discountListItem${index}`]: { visible: index < defaultDisplayedDiscountNo || showAllDiscounts }
            };
        });

        return Object.assign({}, ...listOverride);
    }, [policyDetails, showAllDiscounts]);

    const onToggleShowDiscounts = useCallback(() => {
        updateShowAllDiscounts(!showAllDiscounts);
    }, [showAllDiscounts]);

    const overrides = {
        confirmationBox: {
            className: qbMode ? 'purchase-box vertical-spacing-40 qb' : 'purchase-box vertical-spacing-40'
        },
        ampAdjustedAnnualPremium: {
            visible: ampMode
        },
        premium: {
            content: renderPremium()
        },
        ampStateFeesComponent: {
            visible: ampMode && _.get(policyDetails, 'totalChanges_Ext.amount.value', 0) !== 0,
            totalFees: _.get(policyDetails, 'totalChanges_Ext.value', {}),
            itemizedFees: _.get(policyDetails, 'feesCosts_Ext.value', []),
            displayAlternativeTemplate: false
        },
        ampEffectiveDateContainer: {
            visible: ampMode
        },
        ampEffectiveDateClick: {
            content: renderEffectiveDateClick()
        },
        ampNonEditModeDiv: {
            visible: ampMode
        },
        premiumBeforeChange: {
            content: renderTotalPremium()
        },
        nonEditModeDiv: {
            visible: qbMode
        },
        totalPremium: {
            visible: qbMode,
            content: renderTotalPremium()
        },
        stateFeesComponent: {
            visible: qbMode && _.get(policyDetails, 'totalChanges_Ext.amount.value', 0) !== 0,
            totalFees: _.get(policyDetails, 'totalChanges_Ext.value', {}),
            itemizedFees: _.get(policyDetails, 'feesCosts_Ext.value', []),
            displayAlternativeTemplate: false
        },
        ampAdjustedFullTermPremium: {
            visible: ampMode,
            content: renderCost(_.get(policyDetails, 'fullTermCost_Ext.value', undefined), messages.adjustedFullTermPremium)
        },
        ampAmountDue: {
            visible: ampMode,
            content: renderCost(_.get(policyDetails,'paymentDetails.amountDue.value', undefined), messages.amountDueForChange)
        },
        paymentMethod: {
            content: appendLabelAndVal(translator(messages.paymentMethod), getRatingPaymentMethodDisplay())
        },
        paymentSchedule: {
            visible: !_.isUndefined(paymentScheduleDisplayKey()),
            content: appendLabelAndVal(translator(messages.paymentSchedule), paymentScheduleDisplayKey())
        },
        yourTotalDiscount: {
            visible: qbMode
        },
        ampTotalDiscount: {
            visible: ampMode
        },
        totalDiscounts: {
            visible: qbMode,
            content: appendLabelAndVal(translator(messages.totalDiscount), formatAmountString(totalDiscounts))
        },
        expandAllDiscounts: {
            visible: (!showAllDiscounts && _.get(policyDetails, 'discounts.value', []).length > defaultDisplayedDiscountNo),
            content: (
                <Link onClick={onToggleShowDiscounts}>
                    <span>{translator(messages.seeAllOfMyDiscounts)}</span>&nbsp;
                    <i className="icon-ml-carrot-down-outline" />                   
                </Link>
            )
        },
        hideAllDiscounts: {
            visible: (showAllDiscounts && _.get(policyDetails, 'discounts.value', []).length > defaultDisplayedDiscountNo),
            content: (
                <Link onClick={onToggleShowDiscounts}>
                    <span>{translator(messages.hideDiscounts)}</span>&nbsp;
                    <i className="icon-ml-right-carrot-icon" />                   
                </Link>
            )
        },
        premiumDescTooltip:{
            tooltipContent:translator(messages.whatIsNotIncludedDesc),
            tooltipHeader:translator(messages.whatIsNotIncluded)
        },
        ampPurchaseContainer: {
            visible: ampMode
        },
        ampPolicyChangeEffectiveDate: {
            content: translator(messages.effectiveDateConfirmationText, {date: convertLocalDateToDate(_.get(policyDetails, 'effective_ML.value', undefined))})
        },
        SubmitChangeButton: {
            disabled: !detailsLoaded,
            onClick: submitChanges
        },
        ampCancelChange: {
            visible: ampMode,
            onClick: cancelChanges
        },
        ampMakeMoreChange: {
            visible: ampMode,
            onClick: () => makeMoreChanges('Tiles')
        },
        pleaseNoteContainer: {
            visible: qbMode
        },
        editPaymentAction: {
            href: "",
            className: qbMode ? "remove-txt-decoration" : ""
        },
        ...discountsListOverride()
    };

    const resolvers = {
        resolveCallbackMap: {
            onToggleShowDiscounts: onToggleShowDiscounts,
            onChangePaymentMethodClick: editPayment
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={policyDetails}
            overrideProps={overrides}
            classNameMap={styles}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

PolicySummaryConfirmationBoxComponent.propTypes = {
    model: PropTypes.shape({}).isRequired,
    startPrint: PropTypes.bool,
    editPayment: PropTypes.func.isRequired,
    updateStartPrint: PropTypes.func.isRequired
};

PolicySummaryConfirmationBoxComponent.defaultProps = {
    startPrint: false,
    qbMode: false,
    ampMode: false
};

export default PolicySummaryConfirmationBoxComponent;
